const SALES_DOCTYPES = ["Quotation", "Sales Order", "Delivery Note", "Sales Invoice"];
const PURCHASE_DOCTYPES = ["Supplier Quotation", "Purchase Order", "Purchase Receipt", "Purchase Invoice"];

class TaxesTableUI {
	constructor({ frm }) {
		// setup(frm) { frm._taxes_table_ui = new TaxesTableUI(frm); }
		this.frm = frm;

		/** @type {HTMLDivElement} */
		this._wrapper = null;

		this.tax_template_type = SALES_DOCTYPES.includes(this.frm.doctype)
			? "Sales Taxes and Charges Template"
			: "Purchase Taxes and Charges Template";

		this.hideStandardTable();
		this.disableTaxTemplateField();
	}

	trigger() {
		// Call this function when the `taxes` table changes and in `refresh`
		this.renderTable();
	}

	get wrapper() {
		return (this._wrapper ??= this.makeWrapper());
	}

	makeWrapper() {
		const customWrapper = this.frm?.fields_dict.taxes_html?.$wrapper.get(0);
		if (customWrapper) {
			return customWrapper;
		}

		const standardTable = this.frm?.fields_dict.taxes?.$wrapper.get(0);
		if (standardTable) {
			const div = document.createElement("div");
			standardTable.before(div);
			return div;
		}
	}

	getTaxesAndCharges() {
		return this.frm.doc.taxes;
	}

	hideStandardTable() {
		if (!this.frm?.fields_dict.taxes) return;
		this.frm.fields_dict.taxes.$wrapper.get(0).style.display = "none";
	}

	disableTaxTemplateField() {
		this.frm.toggle_display("taxes_and_charges", false);
	}

	showStandardTable() {
		if (!this.frm?.fields_dict.taxes) return;
		this.frm.fields_dict.taxes.wrapper.style.display = "";
	}

	toggleStandardTable() {
		if (this.frm?.fields_dict.taxes?.wrapper.style.display) {
			this.showStandardTable();
		} else {
			this.hideStandardTable();
		}
	}

	async renderTable() {
		if (!this.wrapper) return;
		const div = document.createElement("div");
		this.wrapper.innerHTML = "";
		this.wrapper.append(div);
		div.classList.add("frappe-control", "mb-4");

		/** @type {TaxesAndCharges[]} */
		const taxes = this.getTaxesAndCharges();

		const rows = [];
		// const allAccounts = new Set(taxes.map(x => x.account_head));

		for (const tax of taxes) {
			const row = new TaxesTableUiRow(tax, this);
			if (!tax.row_id) {
				row.break = true;
			}
			rows.push(row);
		}

		let currentTitle = null;
		for (const row of rows) {
			if (row.title && row.title != currentTitle) {
				currentTitle = row.title;

				const row_title_wrapper = document.createElement("div");
				row_title_wrapper.classList.add("flex", "justify-content-end");

				const row_title = document.createElement("label");
				row_title.classList.add("control-label", "taxes-row-title");
				row_title.textContent = row.title;
				row_title_wrapper.append(row_title);
				div.append(row_title_wrapper);
			}
			const rowElem = await row.render();
			div.append(rowElem);
		}

		const footer = document.createElement("div");
		footer.classList.add("my-4", "flex", "flex-wrap", "rounded", "justify-content-end");
		footer.style.gap = "0.5em";
		div.append(footer);

		const addTemplateButton = document.createElement("button");
		addTemplateButton.classList.add("btn", "btn-default");
		addTemplateButton.innerHTML = frappe.utils.icon("add", "md");
		addTemplateButton.setAttribute("title", __("Add other taxes and charges"));
		addTemplateButton.addEventListener("click", () => {
			this.showSelectTemplateDialog();
		});

		const toggleTableButton = document.createElement("button");
		toggleTableButton.classList.add("btn", "btn-default");
		toggleTableButton.innerHTML = frappe.utils.icon("expand", "md");
		toggleTableButton.setAttribute("title", __("Toggle the detailed tax table"));
		toggleTableButton.addEventListener("click", () => {
			this.toggleStandardTable();
		});

		if (
			this.frm.doc.docstatus == 0 ||
			(this.frm.doc.docstatus == 1 && this.frm.doc.taxes && this.frm.doc.taxes.length)
		) {
			footer.append(toggleTableButton);
		} else {
			const message = document.createElement("div");
			message.classList.add("text-muted", "small", "font-italic", "flex", "align-center");
			message.textContent = __("This invoice has no taxes");
			footer.append(message);
		}

		if (this.frm.doc.docstatus == 0) {
			footer.append(addTemplateButton);
		}
	}

	showSelectTemplateDialog() {
		const dialog = new frappe.ui.Dialog({
			fields: [
				{
					fieldtype: "Link",
					fieldname: "template_name",
					label: __("Taxes and Charges Template"),
					options: this.tax_template_type,
					reqd: true,
					get_query: () => {
						return {
							filters: {
								company: this.frm.doc.company,
								is_default: 0,
								disabled: 0,
							},
						};
					},
				},
			],
			primary_action_label: __("Add"),
			primary_action: async (values) => {
				dialog.hide();
				await this.addTemplateByName(values.template_name);
			},
		});
		dialog.show();
	}

	async addTemplateByName(templateName) {
		/** @type {{ name: string; taxes: TaxesAndCharges[]; }} */
		const templateDoc = await frappe.db.get_doc(this.tax_template_type, templateName);

		// Remove empty tax rows before adding new ones
		const taxes = this.getTaxesAndCharges();
		for (let i = taxes.length - 1; i >= 0; i--) {
			const row = taxes[i];
			// A row is considered empty if account_head is not set
			if (!row.account_head) {
				// @dokos: the grid is not rendered when the taxes table is hidden
				await erpnext.utils.remove_child_row(this.frm, row);
			}
		}

		let offset = this.getTaxesAndCharges().length;
		for (const tax of templateDoc.taxes) {
			const row = this.frm.add_child("taxes");
			if (tax.row_id) {
				tax.row_id = cint(tax.row_id) + offset - 1;
			}
			frappe.model.set_value(row.doctype, row.name, pick(tax, TAX_TEMPLATE_KEYS));
			frappe.model.set_value(row.doctype, row.name, "title", templateDoc.title);
			offset += 1;
		}
		this.frm.refresh_field("taxes");
	}
}

erpnext.TaxesTableUI = TaxesTableUI;

class TaxesTableUiRow {
	constructor(/** @type {TaxesAndCharges} */ taxRow, /** @type {TaxesTableUI} */ table) {
		this.tax = taxRow;
		this.break = false;
		this.table = table;
		this.title = taxRow.title;
	}

	async render() {
		const div = document.createElement("div");
		this.rowElement = div;

		div.classList.add("flex", "align-center", "p-2");
		div.style.gap = "1em";

		if (this.break) {
			div.classList.add("mt-2");
		}

		const writeable =
			frappe.perm.get_field_display_status(this.table.frm.fields_dict.taxes.df, this.table.frm.doc) ==
			"Write";

		const isVAT = await this.is_vat_account();

		const isAmountWrite = this.tax.charge_type == "Actual";
		const isAmountReadOnly = this.tax.charge_type != "Actual";
		const isRateWrite = false; // this.tax.charge_type != "Actual";
		const isItemVAT = isVAT && this.tax.rate == 0;

		const right = document.createElement("div");
		right.classList.add("flex", "align-center");
		right.style.gap = "0.5em";
		right.style.marginLeft = "auto";
		div.append(right);

		const desc = document.createElement("div");
		right.append(desc);
		desc.textContent = this.tax.description;

		// @dokos: the tax category is a neutral label, not a good/warning status — a shared pill,
		// gray for a document-level tax, blue for an item-level one.
		if (isVAT) {
			right.insertAdjacentHTML(
				"beforeend",
				erpnext.dokos.ui.pill.html({
					label: isItemVAT ? __("Item Tax") : __("Tax"),
					theme: isItemVAT ? "blue" : "gray",
					css_class: "whitespace-nowrap",
				})
			);
		}

		if (isRateWrite) {
			this.appendControl(__("Tax Rate"), "rate", {
				fieldtype: "Percent",
				read_only: !writeable,
				reqd: 1,
			});
		}

		if (isAmountReadOnly) {
			this.appendControl(__("Tax Amount"), "tax_amount", { fieldtype: "Currency", read_only: 1 });
		} else if (isAmountWrite) {
			this.appendControl(__("Tax Amount"), "tax_amount", {
				fieldtype: "Currency",
				read_only: !writeable,
				reqd: 1,
			});
		}

		// const accountHead = document.createElement("div");
		// right.append(accountHead);
		// accountHead.innerHTML = frappe.format(this.tax.account_head, { fieldtype: "Link", options: "Account" }, null, this.table.frm.doc);
		// accountHead.firstChild.classList.add("small");

		if (writeable) {
			const deleteButton = document.createElement("button");
			deleteButton.classList.add("btn", "btn-default", "icon-btn");
			deleteButton.innerHTML = frappe.utils.icon("trash", "xs");
			deleteButton.addEventListener("click", this.deleteRow.bind(this));
			div.append(deleteButton);
		}

		return div;
	}

	deleteRow() {
		const dependencies = [];

		for (const row of this.table.getTaxesAndCharges()) {
			if (String(row.row_id) === String(this.tax.idx)) {
				// Register the computed rate dependency
				dependencies.push(row.name);
			}

			if (row.idx >= this.tax.idx) {
				// Renumerate row_id of next rows
				if (row.row_id || row.row_id == "0") {
					row.row_id -= 1;
					// frappe.model.set_value()
				}
			}
		}

		const toDelete = new Set([...dependencies, this.tax.name]);
		// TODO: Don't assume dependencies are after source row
		const N = this.table.getTaxesAndCharges().length;
		const gridRows = this.table.frm.fields_dict.taxes.grid.grid_rows;
		for (let i = N - 1; i >= 0; i--) {
			if (toDelete.has(gridRows[i].doc.name)) {
				gridRows[i].remove();
			}
		}
	}

	/** @private */
	appendControl(/** @type {string} */ label, /** @type {string} */ key, /** @type {object} */ df) {
		const wrapper = document.createElement("div");
		this.rowElement.append(wrapper);

		const control = frappe.ui.form.make_control({
			parent: wrapper,
			render_input: 1,
			only_input: 1,
			df: {
				fieldname: key,
				placeholder: label,
				default: this.tax[key],
				change: () => {
					const value = control.get_value();
					if (
						value &&
						Object.prototype.hasOwnProperty.call(this.tax, key) /* @dokos: lint */ &&
						value !== this.tax[key]
					) {
						frappe.model.set_value(this.tax.doctype, this.tax.name, key, value);
					}
				},
				...df,
			},
		});
		control.set_input(this.tax[key]);
		control.input_area.style.margin = "0px";
		return control;
	}

	async is_vat_account() {
		const result = await frappe.db.get_value("Account", this.tax.account_head, "account_type");
		return result?.message?.account_type == "Tax";
	}
}

/*
  frappe.ui.form.make_control({
    df: {
      fieldtype: "DateRange",
      fieldname: "selected_date_range",
      placeholder: __("Date Range"),
      default: [frappe.datetime.month_start(), frappe.datetime.now_date()],
      input_class: "input-xs",
      reqd: 1,
      change: () => {
        this.selected_date_range = date_field.get_value();
        if (this.selected_date_range) this.make_request();
      },
    },
    parent: $(this.parent).find(".from-date-field"),
    render_input: 1,
  });
*/

function pick(obj, keys) {
	const result = {};
	for (const key of keys) {
		if (key in obj) {
			result[key] = obj[key];
		}
	}
	return result;
}

const TAX_TEMPLATE_KEYS = [
	"account_currency",
	"account_head",
	"base_tax_amount_after_discount_amount",
	"base_tax_amount",
	"base_total",
	"charge_type",
	"cost_center",
	"description",
	"dont_recompute_tax",
	"included_in_paid_amount",
	"included_in_print_rate",
	"item_wise_tax_detail",
	"rate",
	"row_id",
	"tax_amount_after_discount_amount",
	"tax_amount",
	"total",
];

/**
 * @typedef {object} TaxesAndCharges
 * @property {string} charge_type
 * @property {string} row_id
 * @property {string} account_head
 * @property {string} description
 * @property {string} included_in_print_rate
 * @property {string} included_in_paid_amount
 * @property {string} accounting_dimensions_section
 * @property {string} cost_center
 * @property {string} rate
 * @property {string} account_currency
 * @property {string} tax_amount
 * @property {string} total
 * @property {string} tax_amount_after_discount_amount
 * @property {string} base_tax_amount
 * @property {string} base_total
 * @property {string} base_tax_amount_after_discount_amount
 * @property {string} item_wise_tax_detail
 * @property {string} dont_recompute_tax
 */
